import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, X } from 'lucide-react';
import { useMFA } from '@/hooks/useMFA';

const BANNER_DISMISSED_KEY = 'mfa-banner-dismissed';

/**
 * Top warning banner encouraging users to set up MFA.
 *
 * #1515: dismissal is permanent — a security nag that reappears every 7 days
 * reads as an unfinished product (ROF F-A6). Settings carries the persistent
 * MFA status; this banner asks exactly once per browser.
 */
export function MFASetupBanner() {
  const { hasEnrolledMFA, isLoading } = useMFA();
  const [isDismissed, setIsDismissed] = useState(true); // Start hidden to prevent flash

  useEffect(() => {
    setIsDismissed(localStorage.getItem(BANNER_DISMISSED_KEY) !== null);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, new Date().toISOString());
    setIsDismissed(true);
  };

  // Don't show if loading, already has MFA, or dismissed
  if (isLoading || hasEnrolledMFA || isDismissed) {
    return null;
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-900 truncate">
            <span className="hidden sm:inline">Your account is at greater risk because you don't have 2-Step Verification. Turn it on now for extra security.</span>
            <span className="sm:hidden">Enable 2-Step Verification for extra security.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to="/settings">
            <Button variant="outline" size="sm" className="h-7 text-xs border-amber-300 bg-white hover:bg-amber-50 text-amber-900">
              Get started
            </Button>
          </Link>
          <button
            onClick={handleDismiss}
            className="text-amber-500 hover:text-amber-700 p-1"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
